import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import CommonLoader from '../../../../../components/Loaders/loader';
import { Layers } from 'lucide-react';
import { SpoolDetailModal } from './SpoolDetailModal';
import { SP_HEX, spoolTw } from './spoolUiPalette';
import { getNumberOfSpoolsFromPipe } from '../../../types/setup';
import * as spoolsbaseActions from '../../../../../redux/Spoolsbase/actions';

// ─── exported types (used by SpoolDetailModal) ────────────────────────────────
export type SpoolStatus = 'fat_complete' | 'fat_in_progress' | 'in_fabrication' | 'not_started';

export interface WeldFitUp {
  bevelAngle: number;
  rootGap: number;
  hiLo: number;
  bevelMethod: string;
  inspector: string;
  date: string;
  status: 'pass' | 'fail' | 'pending';
}

export interface WeldNDT {
  method: string;
  reportNo: string;
  operator: string;
  date: string;
  result: 'pass' | 'fail';
  /** Count of indications (0 = clear) */
  indications?: number;
}

/** UT/RT indication detail when a report fails (repair workflow). */
export interface WeldIndicationDetail {
  title: string;
  type: string;
  position: string;
  lengthMm: string;
  zone: string;
  disposition: string;
}

/** Defaults for the expanded repair entry form (mock / saved draft). */
export interface WeldRepairFormDefaults {
  triggeringNdtReport?: string;
  defectType?: 'slag' | 'porosity' | 'lof' | 'lop' | 'crack' | 'root_conc' | 'undercut';
  defectLocation?: string;
  defectLengthMm?: string;
  defectDepthMm?: string;
  repairProcedureRef?: string;
  wpsReweld?: 'wps001_same' | 'wps002';
  repairAttempt?: 'attempt1' | 'attempt2';
  excavationDepthMm?: string;
  excavationNdtMethod?: 'mpi' | 'pt' | 'vt';
  excavationNdtResult?: 'pass' | 'fail';
  repairDateIso?: string;
  /** Welder dropdown value — should match `SignaturePanelProps.welderList[].value` when possible */
  repairedByWelderValue?: string;
  preheatTempC?: string;
  interpassTempC?: string;
  visualAfterRepair?: 'pass' | 'fail' | 'pending';
  qcInspector?: string;
  buyerNotified?: 'yes' | 'no';
  buyerApprovedRepair?: 'yes' | 'awaiting';
  notes?: string;
}

export interface WeldRepairSummary {
  recordId: string;
  method: string;
  repairedBy: string;
  date: string;
  ncr: string;
  /** Field defaults for the full repair form UI */
  form?: WeldRepairFormDefaults;
}

export interface WeldRetest {
  title: string;
  operator: string;
  date: string;
  indications: string;
  resultNote: string;
}

/** One saved step-panel row from the backend (mirrors `WeldStepPanelApiDto`). */
export interface WeldStepPanelItem {
  id: number;
  submissionMode: string;
  payload: Record<string, unknown> | null;
  updatedAt: string | null;
}

/** All step panels keyed per weld joint (mirrors `WeldStepPanelsDto` — every slot is an array). */
export interface WeldStepPanels {
  fitUp: WeldStepPanelItem[];
  preheat: WeldStepPanelItem[];
  welding: WeldStepPanelItem[];
  vt: WeldStepPanelItem[];
  ndt: WeldStepPanelItem[];
  coating: WeldStepPanelItem[];
  accept: WeldStepPanelItem[];
}

/** Server-driven repair / retest routing for the weld workflow modal (see `repairWorkflowHelpers.ts`). */
export type WeldWorkflowRepairPhase =
  | 'awaiting_repair_weld'
  | 'awaiting_vt_retest'
  | 'awaiting_ndt_retest';

export interface WeldWorkflowRepairState {
  phase: WeldWorkflowRepairPhase;
  /** Which step recorded the failure that started this cycle */
  sourceStep?: 'vt' | 'ndt';
}

export interface WeldJoint {
  id: string;
  number: string;
  type: string;
  welder: string;
  status: 'accepted' | 'ndt_pass' | 'welded' | 'pending';
  date?: string;
  wps?: string;
  process?: string;
  sideADesc?: string;
  sideBDesc?: string;
  sideAHeat?: string;
  sideBHeat?: string;
  consumableLot?: string;
  fitUp?: WeldFitUp;
  ndt?: WeldNDT[];
  /** Shown when UT/RT has a repair path (mock rich record). */
  indication?: WeldIndicationDetail;
  repair?: WeldRepairSummary;
  retest?: WeldRetest;
  /** e.g. "NDT STATUS: ALL PASS (after repair + retest)" */
  ndtOutcomeLine?: string;
  /** One-line VT summary when not using per-line VT checks */
  vtSummaryLine?: string;
  /** Raw step-panel rows from the API — used for step-progression logic. */
  weldStepPanels?: WeldStepPanels | null;
  /** Numeric sort position within the spool (from DB `sort_order`). */
  sortOrder?: number;
  /** From GET spools workflow load — which step the joint is in (e.g. fitup, preheat, welding). */
  workflowStatus?: string;
  /**
   * When set, the UI opens the matching step until the backend clears or advances the cycle.
   * Typical flow: failed VT/NDT → `awaiting_repair_weld` → after repair submit → `awaiting_vt_retest` or `awaiting_ndt_retest`.
   */
  workflowRepair?: WeldWorkflowRepairState | null;
}

export interface SpoolCard {
  id: number;
  name: string;
  status: SpoolStatus;
  type: string;
  shape: string;
  length: string;
  welds: number;
  welding: number;
  ndt: number;
  coating: number;
  fat: { done: number; total: number } | null;
  weldJoints: WeldJoint[];
  /** The pipe this spool belongs to (from API). */
  pipeId?: number;
  /** Shapes from `getAllSpools` (optional client mirror of production row / setup). */
  spoolDetails?: Record<string, unknown>;
}

// ─── interfaces ───────────────────────────────────────────────────────────────
interface SpoolsProductionProps {
  selectedPipe?: any;
  project?: any;
  canEdit?: boolean;
  canSign?: boolean;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: number;
  isAdminOrSuperAdmin?: boolean;
}

// ─── status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<SpoolStatus, { label: string; className: string }> = {
  fat_complete:    { label: 'FAT Complete',    className: 'bg-green-100 text-green-700 border-green-200' },
  fat_in_progress: { label: 'FAT In Progress', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  in_fabrication:  { label: 'In Fabrication',  className: 'bg-blue-100 text-blue-700 border-blue-200' },
  not_started:     { label: 'Not Started',     className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

// ─── progress helpers (palette aligned with production cards mock) ───────────
function getProgressBarColor(pct: number): string {
  if (pct >= 100) return spoolTw.progCompleteBar;
  if (pct > 0) return spoolTw.progOrangeBar;
  return 'bg-transparent';
}
function getProgressTextColor(pct: number): string {
  if (pct >= 100) return spoolTw.progCompleteText;
  if (pct > 0) return spoolTw.progOrangeText;
  return 'text-gray-400';
}

// ─── rich mock data ───────────────────────────────────────────────────────────
const SPOOL_MOCK_DATA: Record<SpoolStatus, {
  welding: number; ndt: number; coating: number;
  fat: { done: number; total: number } | null;
  shape: string;
  weldJoints: WeldJoint[];
}> = {
  fat_complete: {
    welding: 100, ndt: 100, coating: 100, fat: { done: 12, total: 12 }, shape: 'L-Shape',
    weldJoints: [
      {
        id: 'w1', number: 'W-1', type: 'Butt', welder: 'R. Mohammed (WLD-2024-089)', status: 'accepted',
        date: '15 Mar 2026', wps: 'WPS-001 Rev B', process: 'GMAW',
        sideADesc: '26" OD x 0.75" WT F65', sideBDesc: '26" OD x 0.75" WT X65',
        sideAHeat: 'HN-45231', sideBHeat: 'HN-45232', consumableLot: 'LOT-8821',
        fitUp: { bevelAngle: 30.0, rootGap: 3.0, hiLo: 1.2, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '14 Mar 2026', status: 'pass' },
        ndt: [
          { method: 'RT', reportNo: 'NDT-RT-2026-031', operator: 'A. Singh (L-II)', date: '16 Mar', result: 'pass', indications: 0 },
          { method: 'UT', reportNo: 'NDT-UT-2026-032', operator: 'A. Singh (L-II)', date: '16 Mar', result: 'fail', indications: 1 },
        ],
        indication: {
          title: 'Indication #1 — UT-032',
          type: 'Slag inclusion',
          position: "6 o'clock",
          lengthMm: '8',
          zone: 'Root',
          disposition: 'Repair required',
        },
        repair: {
          recordId: 'REP-W1-001',
          method: 'Grind out + re-weld',
          repairedBy: 'R. Mohammed',
          date: '17 Mar 2026',
          ncr: 'NCR-003',
          form: {
            triggeringNdtReport: 'NDT-UT-2026-032',
            defectType: 'slag',
            defectLocation: "6 o'clock, root zone",
            defectLengthMm: '8',
            defectDepthMm: '3.5',
            repairProcedureRef: '',
            wpsReweld: 'wps001_same',
            repairAttempt: 'attempt1',
            excavationDepthMm: '5.0',
            excavationNdtMethod: 'mpi',
            excavationNdtResult: 'pass',
            repairDateIso: '2026-03-17',
            repairedByWelderValue: undefined,
            preheatTempC: '110',
            interpassTempC: '220',
            visualAfterRepair: 'pass',
            qcInspector: 'J. Garcia',
            buyerNotified: 'yes',
            buyerApprovedRepair: 'yes',
            notes:
              'Slag inclusion fully excavated at 5mm depth. MPI confirmed clean excavation. Re-welded per WPS-001 Rev B with same filler lot.',
          },
        },
        retest: {
          title: 'Retest — NDT-UT-2026-033 (Pass)',
          operator: 'A. Singh (L-II)',
          date: '18 Mar 2026',
          indications: '0 (clear)',
          resultNote: 'Pass',
        },
        ndtOutcomeLine: 'NDT STATUS: ALL PASS (after repair + retest)',
      },
      {
        id: 'w2', number: 'W-2', type: 'Butt', welder: 'R. Mohammed', status: 'accepted',
        date: '16 Mar 2026', wps: 'WPS-001 Rev B', sideAHeat: 'HN-45232', sideBHeat: 'HN-45233', consumableLot: 'LOT-8821',
        fitUp: { bevelAngle: 30.0, rootGap: 2.8, hiLo: 1.0, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '15 Mar 2026', status: 'pass' },
        ndt: [
          { method: 'RT', reportNo: 'NDT-RT-2026-033', operator: 'A. Singh (L-II)', date: '17 Mar', result: 'pass' },
          { method: 'UT', reportNo: 'NDT-UT-2026-034', operator: 'A. Singh (L-II)', date: '17 Mar', result: 'pass' },
        ],
      },
      {
        id: 'w3', number: 'W-3', type: 'Butt', welder: 'R. Mohammed', status: 'accepted',
        date: '18 Mar 2026', wps: 'WPS-001 Rev B', sideAHeat: 'HN-45233', sideBHeat: 'HN-45234', consumableLot: 'LOT-8821',
        fitUp: { bevelAngle: 30.0, rootGap: 3.2, hiLo: 1.1, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '17 Mar 2026', status: 'pass' },
        ndt: [{ method: 'RT', reportNo: 'NDT-RT-2026-035', operator: 'A. Singh (L-II)', date: '19 Mar', result: 'pass' }],
      },
      {
        id: 'w4', number: 'W-4', type: 'Butt', welder: 'K. Patel', status: 'accepted',
        date: '19 Mar 2026', wps: 'WPS-002 Rev A', sideAHeat: 'HN-45234', sideBHeat: 'HN-45235', consumableLot: 'LOT-8822',
        fitUp: { bevelAngle: 30.0, rootGap: 2.9, hiLo: 1.3, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '18 Mar 2026', status: 'pass' },
        ndt: [{ method: 'RT', reportNo: 'NDT-RT-2026-036', operator: 'A. Singh (L-II)', date: '20 Mar', result: 'pass' }],
      },
      {
        id: 'w5', number: 'W-5', type: 'Fillet', welder: 'K. Patel', status: 'accepted',
        date: '20 Mar 2026', wps: 'WPS-002 Rev A', sideAHeat: 'HN-45235', sideBHeat: 'HN-45236', consumableLot: 'LOT-8822',
        fitUp: { bevelAngle: 45.0, rootGap: 0, hiLo: 0.8, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '19 Mar 2026', status: 'pass' },
        ndt: [{ method: 'MPI', reportNo: 'NDT-MPI-2026-010', operator: 'A. Singh (L-II)', date: '21 Mar', result: 'pass' }],
      },
    ],
  },

  fat_in_progress: {
    welding: 100, ndt: 100, coating: 100, fat: { done: 7, total: 12 }, shape: 'Straight',
    weldJoints: [
      {
        id: 'w1', number: 'W-1', type: 'Butt', welder: 'R. Mohammed', status: 'ndt_pass',
        date: '20 Mar 2026', wps: 'WPS-001 Rev B', sideAHeat: 'HN-45240', sideBHeat: 'HN-45241', consumableLot: 'LOT-8823',
        fitUp: { bevelAngle: 30.0, rootGap: 3.1, hiLo: 1.4, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '19 Mar 2026', status: 'pass' },
        ndt: [
          { method: 'RT', reportNo: 'NDT-RT-2026-040', operator: 'A. Singh (L-II)', date: '21 Mar', result: 'pass' },
          { method: 'UT', reportNo: 'NDT-UT-2026-041', operator: 'A. Singh (L-II)', date: '21 Mar', result: 'pass' },
        ],
      },
      {
        id: 'w2', number: 'W-2', type: 'Butt', welder: 'R. Mohammed', status: 'ndt_pass',
        date: '22 Mar 2026', wps: 'WPS-001 Rev B', sideAHeat: 'HN-45241', sideBHeat: 'HN-45242', consumableLot: 'LOT-8823',
        fitUp: { bevelAngle: 30.0, rootGap: 2.7, hiLo: 1.1, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '21 Mar 2026', status: 'pass' },
        ndt: [{ method: 'RT', reportNo: 'NDT-RT-2026-042', operator: 'A. Singh (L-II)', date: '23 Mar', result: 'pass' }],
      },
      {
        id: 'w3', number: 'W-3', type: 'Butt', welder: 'R. Mohammed', status: 'welded',
        date: '06 Apr 2026', wps: 'WPS-001 Rev B', process: 'GMAW',
        sideAHeat: 'HN-45233', sideBHeat: 'HN-45234', consumableLot: 'LOT-8822',
        fitUp: { bevelAngle: 30.0, rootGap: 2.8, hiLo: 1.0, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '05 Apr 2026', status: 'pass' },
        vtSummaryLine: 'VT: PASS — J. Garcia, 06 Apr 2026',
      },
      {
        id: 'w4', number: 'W-4', type: 'Butt', welder: '—', status: 'pending',
      },
    ],
  },

  in_fabrication: {
    welding: 75, ndt: 50, coating: 0, fat: null, shape: 'Straight',
    weldJoints: [
      {
        id: 'w1', number: 'W-1', type: 'Butt', welder: 'R. Mohammed', status: 'ndt_pass',
        date: '25 Mar 2026', wps: 'WPS-001 Rev B', sideAHeat: 'HN-45250', sideBHeat: 'HN-45251', consumableLot: 'LOT-8825',
        fitUp: { bevelAngle: 30.0, rootGap: 3.0, hiLo: 1.2, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '24 Mar 2026', status: 'pass' },
        ndt: [{ method: 'RT', reportNo: 'NDT-RT-2026-050', operator: 'A. Singh (L-II)', date: '26 Mar', result: 'pass' }],
      },
      {
        id: 'w2', number: 'W-2', type: 'Butt', welder: 'R. Mohammed', status: 'ndt_pass',
        date: '27 Mar 2026', wps: 'WPS-001 Rev B', sideAHeat: 'HN-45251', sideBHeat: 'HN-45252', consumableLot: 'LOT-8825',
        fitUp: { bevelAngle: 30.0, rootGap: 2.9, hiLo: 1.3, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '26 Mar 2026', status: 'pass' },
        ndt: [{ method: 'UT', reportNo: 'NDT-UT-2026-051', operator: 'A. Singh (L-II)', date: '28 Mar', result: 'pass' }],
      },
      {
        id: 'w3', number: 'W-3', type: 'Butt', welder: 'R. Mohammed', status: 'welded',
        date: '06 Apr 2026', wps: 'WPS-001 Rev B', sideAHeat: 'HN-45252', sideBHeat: 'HN-45253', consumableLot: 'LOT-8826',
        fitUp: { bevelAngle: 30.0, rootGap: 2.8, hiLo: 1.0, bevelMethod: 'Cold Cut', inspector: 'J. Garcia', date: '05 Apr 2026', status: 'pass' },
      },
      {
        id: 'w4', number: 'W-4', type: 'Butt', welder: '—', status: 'pending',
      },
    ],
  },

  not_started: {
    welding: 0, ndt: 0, coating: 0, fat: null, shape: 'Straight',
    weldJoints: [
      { id: 'w1', number: 'W-1', type: 'Butt', welder: '—', status: 'pending' },
      { id: 'w2', number: 'W-2', type: 'Butt', welder: '—', status: 'pending' },
      { id: 'w3', number: 'W-3', type: 'Butt', welder: '—', status: 'pending' },
      { id: 'w4', number: 'W-4', type: 'Butt', welder: '—', status: 'pending' },
    ],
  },
};

const STATUS_SEQUENCE: SpoolStatus[] = [
  'fat_complete', 'fat_in_progress', 'in_fabrication', 'not_started',
];

function buildMockSpools(count: number): SpoolCard[] {
  return Array.from({ length: count }, (_, i) => {
    const status: SpoolStatus = STATUS_SEQUENCE[i] ?? 'not_started';
    const data = SPOOL_MOCK_DATA[status];
    return {
      id:     i + 1,
      name:   `SPOOL-${i + 1}`,
      status,
      type:   'Prefab',
      length: '29.58 m',
      welds:  i === 0 ? 5 : 4,
      welding:  data.welding,
      ndt:      data.ndt,
      coating:  data.coating,
      fat:      data.fat,
      shape:    data.shape,
      weldJoints: data.weldJoints,
    };
  });
}

// ─── sub-components ───────────────────────────────────────────────────────────
function ProgressRow({ label, pct, display }: { label: string; pct: number; display: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-16 shrink-0 text-[13px] font-medium" style={{ color: SP_HEX.labelSlate }}>
        {label}
      </span>
      <div
        className="flex-1 h-2 rounded-full overflow-hidden"
        style={{ backgroundColor: SP_HEX.track }}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ${getProgressBarColor(pct)}`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
      <span className={`w-12 text-right font-semibold text-xs ${getProgressTextColor(pct)}`}>
        {display}
      </span>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
export const SpoolsProduction: React.FC<SpoolsProductionProps> = ({
  selectedPipe,
  project,
  canEdit = true,
}) => {
  const dispatch = useDispatch();
  const numberOfSpools: number = getNumberOfSpoolsFromPipe(
    (selectedPipe ?? {}) as Record<string, unknown>
  );
  const { allSpools, allSpoolsLoading, allSpoolsError } = useSelector((state: any) => ({
    allSpools: state.spoolsbase?.allSpools ?? [],
    allSpoolsLoading: state.spoolsbase?.allSpoolsLoading ?? false,
    allSpoolsError: state.spoolsbase?.allSpoolsError ?? null,
  }));

  const projectId = project?.project?.projectId;
  const pipeId = selectedPipe?.pipeId;

  useEffect(() => {
    if (process.env.REACT_APP_SPOOL_TRACKER_USE_MOCK === 'true') {
      return;
    }
    if (projectId == null || projectId === '' || pipeId == null || pipeId === '') {
      return;
    }
    dispatch(
      spoolsbaseActions.fetchAllSpools({
        projectId,
        pipeId,
      })
    );
  }, [dispatch, projectId, pipeId]);

  // When a weld-step save triggers a background refetch, keep selectedSpool
  // in sync so the open modal receives the fresh weldStepPanels data.
  useEffect(() => {
    if (selectedSpool == null || !Array.isArray(allSpools) || allSpools.length === 0) return;
    const updated = (allSpools as SpoolCard[]).find((s) => s.id === selectedSpool.id);
    if (updated && updated !== selectedSpool) {
      setSelectedSpool(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSpools]);

  const mockSpools = useMemo(() => buildMockSpools(numberOfSpools), [numberOfSpools]);
  const useMockFallback = process.env.REACT_APP_SPOOL_TRACKER_USE_MOCK === 'true';
  const spools: SpoolCard[] =
    useMockFallback && !allSpoolsLoading
      ? mockSpools
      : (allSpools as SpoolCard[]);
  const [selectedSpool, setSelectedSpool] = useState<SpoolCard | null>(null);


  if (!selectedPipe) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center justify-center text-center gap-4">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center">
                <Layers className="h-10 w-10 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Spools Production</h3>
                <p className="text-gray-500 text-sm max-w-sm leading-relaxed">
                  Select a spool group from the dropdown above to view production data.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!useMockFallback && allSpoolsLoading && (!allSpools || allSpools.length === 0)) {
    return (
      <Card>
        <CardContent className="p-4">
          <CommonLoader />
        </CardContent>
      </Card>
    );
  }

  if (!useMockFallback && allSpoolsError) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col items-center justify-center min-h-[320px] text-center gap-4">
            <p className="text-sm text-red-600 max-w-md">{allSpoolsError}</p>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                projectId != null &&
                pipeId != null &&
                dispatch(spoolsbaseActions.fetchAllSpools({ projectId, pipeId }))
              }
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!useMockFallback && numberOfSpools === 0 && spools.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center justify-center text-center gap-4">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                <Layers className="h-10 w-10 text-gray-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Spools Configured</h3>
                <p className="text-gray-500 text-sm max-w-sm leading-relaxed">
                  No spools have been set up for{' '}
                  <span className="font-medium">
                    {selectedPipe.pipeName || selectedPipe.displayName || 'this pipe'}
                  </span>
                  . Please update the setup to add spools.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!useMockFallback && numberOfSpools > 0 && spools.length === 0 && !allSpoolsLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center justify-center text-center gap-4">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                <Layers className="h-10 w-10 text-gray-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No spool records</h3>
                <p className="text-gray-500 text-sm max-w-md leading-relaxed">
                  The spool tracker API returned no spools for this pipe. If you expect data here,
                  verify project and pipe identifiers on the backend.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header note */}
        {/* <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3">
          <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">
            Production Manager Note
          </p>
          <p className="text-sm text-yellow-800">
            Visual overview of all{' '}
            <span className="font-semibold">{numberOfSpools} spools</span> for{' '}
            <span className="font-semibold">
              {selectedPipe.pipeName || selectedPipe.displayName || 'this pipe'}
            </span>
            . Each card is clickable to drill into the spool's full weld record. This view helps the
            Fabrication Manager see bottlenecks at a glance.
          </p>
        </div> */}

        {/* Spool card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {spools.map((spool, idx) => {
            const statusCfg = STATUS_CONFIG[spool.status];
            return (
              <Card
                key={`${spool.id}-${spool.name}-${idx}`}
                onClick={() => setSelectedSpool(spool)}
                className="hover:shadow-md transition-all cursor-pointer border border-gray-200 group hover:border-[#1E6353]/35"
              >
                {/* Card header */}
                <div className="flex items-center justify-between px-4 pt-4 pb-2">
                  <h4 className="text-sm font-bold text-gray-900 tracking-wide group-hover:text-teal-700 transition-colors">
                    {spool.name}
                  </h4>
                  <span
                    className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${statusCfg.className}`}
                  >
                    {statusCfg.label}
                  </span>
                </div>

                <CardContent className="px-4 pb-4 pt-0 space-y-3">
                  {/* Meta info */}
                  <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                    <dt className="text-gray-400 font-medium">Type</dt>
                    <dd className="text-gray-700 font-semibold">{spool.type}</dd>
                    <dt className="text-gray-400 font-medium">Shape</dt>
                    <dd className="text-gray-700 font-semibold">{spool.shape}</dd>
                    <dt className="text-gray-400 font-medium">Length</dt>
                    <dd className="text-gray-700 font-semibold">{spool.length}</dd>
                    <dt className="text-gray-400 font-medium">Welds</dt>
                    <dd className="text-gray-700 font-semibold">{spool.welds}</dd>
                  </dl>

                  <div className="border-t border-gray-100" />

                  {/* Progress rows */}
                  <div className="space-y-1.5">
                    <ProgressRow
                      label="Welding"
                      pct={spool.welding}
                      display={spool.welding > 0 ? `${spool.welding}%` : '0%'}
                    />
                    <ProgressRow
                      label="NDT"
                      pct={spool.ndt}
                      display={spool.ndt > 0 ? `${spool.ndt}%` : '—'}
                    />
                    <ProgressRow
                      label="Coating"
                      pct={spool.coating}
                      display={spool.coating > 0 ? `${spool.coating}%` : '—'}
                    />
                    <ProgressRow
                      label="FAT"
                      pct={spool.fat ? (spool.fat.done / spool.fat.total) * 100 : 0}
                      display={spool.fat ? `${spool.fat.done}/${spool.fat.total}` : '—'}
                    />
                  </div>

                  <div className={`text-xs text-center font-medium opacity-0 group-hover:opacity-100 transition-opacity pt-1 ${spoolTw.cardHintText}`}>
                    Click to open weld management →
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      

      {/* Detail modal */}
      <SpoolDetailModal
        spool={selectedSpool}
        open={!!selectedSpool}
        onClose={() => setSelectedSpool(null)}
        project={project}
        selectedpipe={selectedPipe}
      />
    </>
  );
};
